import { useEffect } from "react";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useForm, useFieldArray } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { teamGameRegistration } from "../api";
import ValidationSchema from "../config/validationSchema";
import FormInput from "./FormInput";
import { TEAM_SPORTS } from "../config/constant";
import PlayerFormSection from "./PlayerCard";

const schema = Joi.object({
  players: Joi.array()
    .items(
      Joi.object({
        name: Joi.string().required().messages({
          "any.required": "Player name is required.",
          "string.empty": "Player name cannot be empty.",
        }),
        enrollmentNumber: Joi.string().min(5).required().messages({
          "any.required": "Enrollment number is required.",
          "string.empty": "Enrollment number cannot be empty.",
          "string.min": "Enrollment number must be at least 5 characters long.",
        }),
        semester: Joi.string().valid("1", "2", "3", "4", "5", "6", "7", "8").required().messages({
          "any.required": "Semester is required.",
          "string.empty": "Semester cannot be empty.",
          "any.only": "Please select a valid semester.",
        }),
        phone: Joi.string().pattern(/^[0-9]{10}$/).required().messages({
          "any.required": "Phone number is required.",
          "string.empty": "Phone number cannot be empty.",
          "string.pattern.base": "Phone number must be exactly 10 digits long.",
        }),
        tShirtSize: Joi.string().valid("XS", "S", "M", "L", "XL", "XXL").required().messages({
          "any.required": "T-shirt size is required.",
          "string.empty": "T-shirt size cannot be empty.",
          "any.only": "Please select a valid T-shirt size.",
        }),
        role: Joi.string()
          .valid("captain", "vice-captain", "player")
          .required()
          .messages({
            "any.only": "Role must be captain, vice-captain, or player.",
            "any.required": "Role is required.",
          }),
      })
    )
    .required()
    .custom((players, helpers) => {
      // Check if all fields are filled
      for (const [index, player] of players.entries()) {
        if (!player.name) {
          return helpers.message(`Player Name is required.`);
        }
        if (!player.enrollmentNumber) {
          return helpers.message(`Player Enrollment number is required.`);
        }
        if (!player.semester) {
          return helpers.message(`Player Semester is required.`);
        }
        if (!player.phone) {
          return helpers.message(`Player Phone number is required.`);
        }
        if (!player.tShirtSize) {
          return helpers.message(`Player T-Shirt size is required.`);
        }
      }

      // Game-specific validations
      const { name: game } = helpers.state.ancestors[0].sport;
      const roles = players.map((player) => player.role);

      if (roles.filter((role) => role === "captain").length !== 1) {
        return helpers.message("There must be exactly one captain.");
      }

      if (roles.filter((role) => role === "vice-captain").length !== 1) {
        return helpers.message("There must be exactly one vice-captain.");
      }

      const uniqueEnrollmentNumbers = new Set(players.map((p) => p.enrollmentNumber));
      if (uniqueEnrollmentNumbers.size !== players.length) {
        return helpers.message("Enrollment numbers must be unique.");
      }

      const uniquePhoneNumbers = new Set(players.map((p) => p.phone));
      if (uniquePhoneNumbers.size !== players.length) {
        return helpers.message("Phone numbers must be unique.");
      }

      const gameDetails = TEAM_SPORTS.find((sport) => sport.name === game);
      if (!gameDetails) return helpers.message("Invalid game selected.");

      if (players.length < gameDetails.minPlayers) {
        return helpers.message(`Minimum ${gameDetails.minPlayers} players are required.`);
      }
      if (players.length > gameDetails.teamSize) {
        return helpers.message(`Maximum ${gameDetails.teamSize} players allowed.`);
      }

      return players;
    })
    .messages({
      "any.required": "Please add players to the team.",
    }),
  file: Joi.any()
    .custom((value, helpers) => {
      if (!value || !value[0]) return helpers.message("HOD Latter is required.");
      const file = value[0];
      const allowedExtensions = /\.(png|jpe?g)$/i; // Regular expression for allowed extensions
      if (!allowedExtensions.test(file.name)) {
        return helpers.message("Only PNG, JPEG, and JPG files are allowed.");
      }
      if (file.size > 5 * 1024 * 1024) { // 5 MB limit
        return helpers.message("File size must be 5 MB or smaller.");
      }
      return value;
    })
    .messages({
      "any.required": "HOD Latter is required.",
    }),
});


// Main Form Component
export default function TeamRegistrationForm() {
  const location = useLocation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(schema, { stripUnknown: true }),
    defaultValues: { players: [{ role: "captain" }, { role: "vice-captain" }] },
  });

  const { fields, append } = useFieldArray({
    control,
    name: "players",
  });

  const selectedSport = watch("sport");

  const onTeamRegister = async (data) => {
    try {
      await teamGameRegistration({
        game: selectedSport.name,
        players: data.players,
        file: data.file[0],
      });
      toast.success("Registration Successful");
      navigate("/browseGame");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Registration failed");
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const gameFromUrl = queryParams.get("game")?.toLowerCase();
    const sport = TEAM_SPORTS.find((s) => s.name.toLowerCase() === gameFromUrl);
    if (sport) setValue("sport", sport);
  }, [location.search, setValue]);

  useEffect(() => {
    if (errors && errors?.players?.message) {
      toast.error(errors?.players?.message);
    }
    if (errors && errors?.root?.message) {
      toast.error(errors?.root?.message);
    }
  }, [errors])

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="mx-auto max-w-4xl rounded-lg border bg-white shadow-lg">
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white">
          <h1 className="text-3xl font-bold">{selectedSport?.name} Team Registration</h1>
          {selectedSport && (
            <>
              <p>Minimum Players: {selectedSport.minPlayers}</p>
              <p>Maximum Players: {selectedSport.teamSize}</p>
              {selectedSport.link && (
                <Link
                  to={selectedSport.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline"
                >
                  View Rules
                </Link>
              )}
            </>
          )}
        </div>

        <form onSubmit={handleSubmit(onTeamRegister)} className="p-6 space-y-6">
          {!selectedSport && (
            <div className="grid gap-4 md:grid-cols-2">
              {TEAM_SPORTS.map((sport) => (
                <button
                  key={sport.name}
                  onClick={() => setValue("sport", sport)}
                  className={`flex flex-col items-center justify-center rounded-lg border-2 p-6 transition-all duration-300 ${selectedSport?.name === sport.name
                    ? "border-red-500 bg-red-50"
                    : "border-red-200 bg-white hover:border-red-300 hover:bg-red-50/50"
                    }`}
                >
                  <span className="text-lg font-semibold text-red-600">
                    {sport.name}
                  </span>
                  <span className="text-sm text-gray-600">
                    Team Size: {sport.teamSize}
                  </span>
                </button>
              ))}
            </div>
          )}

          {selectedSport && (
            <>
              {fields.map((field, index) => (
                <PlayerFormSection
                  key={field.id}
                  register={register}
                  errors={errors}
                  index={index}
                  role={field.role}
                />
              ))}
              {fields.length < selectedSport.teamSize && (
                <button
                  type="button"
                  onClick={() => append({ role: "player" })}
                  className="w-full rounded-md border border-red-500 bg-white px-4 py-3 text-sm font-semibold text-red-500 transition-all duration-300 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500/20"
                >
                  Add Player
                </button>
              )}
              <div className="rounded-lg border border-red-100 bg-red-50/50 p-4 mb-6">
                <FormInput
                  label="Upload HOD Latter (PNG , JPEG OR JPG only)"
                  type="file"
                  accept=".png,.jpeg,.jpg"
                  register={register("file")}
                  error={errors?.file?.message}
                />
              </div>
            </>
          )}

          {selectedSport && (
            <button
              type="submit"
              className="w-full rounded-lg bg-red-500 px-4 py-2 text-white "
            >
              Submit Team
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
